<template>
  <div
    class="custom-date-picker"
    :data-testid="`${testid}-custom-picker`"
  >
    <PickerHeader
      :label="formattedDateString"
      :testid="testid"
    />
    <PickerControls
      :type="type"
      :testid="testid"
      :formatted-year="formattedYear"
      :formatted-month="formattedMonth"
      :formatted-season="formattedSeason"
      :can-shift-month-prev="canShiftMonthPrev"
      :can-shift-month-next="canShiftMonthNext"
      :can-shift-season-prev="canShiftSeasonPrev"
      :can-shift-season-next="canShiftSeasonNext"
      :can-shift-year-prev="canShiftYearPrev"
      :can-shift-year-next="canShiftYearNext"
      :can-shift-view-year-prev="canShiftViewYearPrev"
      :can-shift-view-year-next="canShiftViewYearNext"
      @shift-year="shiftYear"
      @shift-view-year="shiftViewYear"
      @shift-month="shiftMonth"
      @shift-season="shiftSeason"
      @toggle-view="toggleView"
    />
    <div class="dialog">
      <YearGrid
        v-if="openView === 'year'"
        v-model="pickerOpen"
        :testid="testid"
        :allowed-years="years"
        :selected="selectedYear"
        @select="onYearSelect"
      />
      <SeasonGrid
        v-if="type === 'season' && openView === 'season'"
        :testid="testid"
        :seasons="seasons"
        :selected="selectedSeason"
        :locale="locale"
        :is-disabled="isSeasonDisabled"
        :view-year="viewYear"
        :selected-year="selectedYear"
        @select="onSeasonSelect"
      />
      <MonthGrid
        v-if="type === 'month' && openView === 'month'"
        :testid="testid"
        :selected="selectedMonth"
        :locale="locale"
        :is-disabled="isMonthDisabled"
        :view-year="viewYear"
        :selected-year="selectedYear"
        @select="onMonthSelect"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  watch,
  PropType,
} from 'vue'
import { useI18n } from 'vue-i18n'
import DatasetStore from "@/stores/dataset/dataset-module";
import {
  removeTimezoneOffset,
  temporalResolutions,
  generateRange,
  monthNumber,
  getPickerLabel,
  localeDateString,
  Frequency,
} from '@/util/datetime'

import PickerHeader from '@/components/common/picker/PickerHeader.vue'
import PickerControls from '@/components/common/picker/PickerControls.vue'
import YearGrid from '@/components/common/picker/YearGrid.vue'
import MonthGrid from '@/components/common/picker/MonthGrid.vue'
import SeasonGrid from '@/components/common/picker/SeasonGrid.vue'

export default defineComponent({
  name: 'CustomDatePicker',
  components: {
    PickerHeader,
    PickerControls,
    YearGrid,
    MonthGrid,
    SeasonGrid,
  },
  props: {
    modelValue: {
      type: String as () => string | null,
      default: null,
    },
    min: {
      type: String,
      required: true,
    },
    max: {
      type: String,
      required: true,
    },
    testid: {
      type: String,
      required: true,
    },
    type: {
      type: String as PropType<'year' | 'season' | 'month'>,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const { locale } = useI18n()

    const datasetStore = DatasetStore;
    const freq = ref<Frequency>(datasetStore.metadata!.frequency);
    const internalDate = ref<Date | null>(null);
    const minDate = new Date(props.min);
    const maxDate = new Date(props.max);

    watch(
      () => props.modelValue,
      (val) => {
        if (!val) {
          internalDate.value = null
          return
        }
        const parsed = new Date(val)
        internalDate.value = isNaN(parsed.getTime()) ? null : parsed
      },
      { immediate: true }
    )

    const emitDate = (date: Date) => {
      internalDate.value = date
      emit('update:modelValue', removeTimezoneOffset(date).toISOString().slice(0, 10))
    }

    const selectedYear = computed(() => {
      if (!internalDate.value) return null;
      return internalDate.value.getFullYear();
    })

    const viewYear = ref<number>(internalDate.value?.getFullYear() ?? maxDate.getFullYear())

    const selectedMonth = computed(() => {
      if (!internalDate.value) return null;
      return internalDate.value.getMonth();
    })

    const selectedSeason = computed(() => {
      if (!internalDate.value) return null;
      const m = monthNumber(internalDate.value);
      if ([12, 1, 2].includes(m)) {
        return 12 // Winter
      } else if ([3, 4, 5].includes(m)) {
        return 3 // Spring
      } else if ([6, 7, 8].includes(m)) {
        return 6 // Summer
      } else {
        return 9 // Fall
      }
    })

    type PickerView = 'year' | 'season' | 'month'
    
    const openView = ref<PickerView>(props.type)

    const toggleView = (view: 'year' | 'season' | 'month') => {
    if (view === 'year') {
      if (openView.value !== 'year') {
        openView.value = 'year'
      }
    } else {
      openView.value = view
    }
  }

    const seasons = temporalResolutions.seasons

    const years = computed(() => {
      const start = minDate.getFullYear()
      const end = maxDate.getFullYear()
      return generateRange(start, end, 1).reverse()
    })

    const formattedYear = computed(() => {
      if (openView.value === 'month' || openView.value === 'season') {
        const dummyDate = new Date(viewYear.value, 0, 1)
        return getPickerLabel(dummyDate, locale.value, 'year')
      }

      let date;
      if (!internalDate.value) {
        date = maxDate;
      } else {
        date = internalDate.value;
      }
      return getPickerLabel(date, locale.value, 'year')
    })

    const formattedSeason = computed(() => {
      let date;
      if (!internalDate.value) {
        date = maxDate;
      } else {
        date = internalDate.value;
      }
      return getPickerLabel(date, locale.value, 'season')
    })

    const formattedMonth = computed(() => {
      let date;
      if (!internalDate.value) {
        date = maxDate;
      } else {
        date = internalDate.value;
      }
      return getPickerLabel(date, locale.value, 'month')
    })

    const formattedDateString = computed(() => {
      if (!internalDate.value) return '';
      return localeDateString(internalDate.value, locale.value, freq.value)
    })

    const onYearSelect = (year: number) => {
      let month = 0; // default to January, for type year
      if (props.type === 'month' || props.type === 'season') {
        month = internalDate.value ? internalDate.value.getMonth() : maxDate.getMonth();
      }
      const newDate = new Date(year, month, 1);
      viewYear.value = year;

      if (props.type === 'month') {
        openView.value = 'month';
      } else if (props.type === 'season') {
        openView.value = 'season';
      } else {
        emitDate(newDate);
      }
    }

    const onMonthSelect = (month: number) => {
      const newDate = new Date(viewYear.value, month - 1, 1);
      openView.value = 'month';
      emitDate(newDate);
    }

    const onSeasonSelect = (season: { value: number }) => {
      const newDate = new Date(viewYear.value, season.value - 1, 1);
      openView.value = 'season';
      emitDate(newDate);
    }

    const shiftViewYear = (delta: number) => {
      viewYear.value += delta
    }

    const shiftYear = (delta: number) => {
      const base = internalDate.value ?? new Date();
      const newDate = new Date(base);
      newDate.setFullYear(base.getFullYear() + delta);
      emitDate(newDate);
    }

    const shiftMonth = (delta: number) => {
      const d = internalDate.value ?? new Date();
      const newDate = new Date(d.getFullYear(), d.getMonth() + delta, 1);
      viewYear.value = newDate.getFullYear();
      emitDate(newDate);
    }

    const shiftSeason = (delta: number) => {
      const d = internalDate.value ?? new Date()
      const newDate = new Date(d.getFullYear(), d.getMonth() + delta * 3, 1);
      viewYear.value = newDate.getFullYear();
      emitDate(newDate);
    }

    const startOfMonth = (date: Date) => {
      return new Date(date.getFullYear(), date.getMonth(), 1)
    }

    const isMonthDisabled = (month: number) => {
      const date = new Date(viewYear.value, month - 1, 1)
      return date < startOfMonth(minDate) || date > startOfMonth(maxDate)
    }

    const isSeasonDisabled = (season: { value: number }) => {
      const date = new Date(viewYear.value, season.value - 1, 1)
      return date < startOfMonth(minDate) || date > startOfMonth(maxDate)
    }

    const canShiftMonthPrev = computed(() => {
      if (!internalDate.value) return false
      const current = startOfMonth(internalDate.value)
      const prev = new Date(current.getFullYear(), current.getMonth() - 1, 1)
      return prev >= startOfMonth(minDate)
    })

    const canShiftMonthNext = computed(() => {
      if (!internalDate.value) return false
      const current = startOfMonth(internalDate.value)
      const next = new Date(current.getFullYear(), current.getMonth() + 1, 1)
      return next <= startOfMonth(maxDate)
    })

    const canShiftSeasonPrev = computed(() => {
      if (!internalDate.value) return false
      const current = startOfMonth(internalDate.value)
      const prev = new Date(current.getFullYear(), current.getMonth() - 3, 1)
      return prev >= startOfMonth(minDate)
    })

    const canShiftSeasonNext = computed(() => {
      if (!internalDate.value) return false
      const current = startOfMonth(internalDate.value)
      const next = new Date(current.getFullYear(), current.getMonth() + 3, 1)
      return next <= startOfMonth(maxDate)
    })

    const canShiftYearPrev = computed(() => {
      if (!internalDate.value) return false
      const prev = new Date(internalDate.value.getFullYear() - 1, 0, 1)
      return prev >= new Date(minDate.getFullYear(), 0, 1)
    })

    const canShiftYearNext = computed(() => {
      if (!internalDate.value) return false
      const next = new Date(internalDate.value.getFullYear() + 1, 0, 1)
      return next <= new Date(maxDate.getFullYear(), 0, 1)
    })

    const canShiftViewYearPrev = computed(() => {
      return viewYear.value - 1 >= minDate.getFullYear()
    })

    const canShiftViewYearNext = computed(() => {
      return viewYear.value + 1 <= maxDate.getFullYear()
    })

    return {
      locale,
      seasons,
      years,
      selectedYear,
      selectedMonth,
      selectedSeason,
      formattedYear,
      formattedSeason,
      formattedMonth,
      formattedDateString,
      openView,
      toggleView,
      onYearSelect,
      onMonthSelect,
      onSeasonSelect,
      shiftYear,
      shiftViewYear,
      shiftMonth,
      shiftSeason,
      isMonthDisabled,
      isSeasonDisabled,
      viewYear,
      canShiftMonthPrev,
      canShiftMonthNext,
      canShiftSeasonPrev,
      canShiftSeasonNext,
      canShiftYearPrev,
      canShiftYearNext,
      canShiftViewYearPrev,
      canShiftViewYearNext,
    }
  },
})
</script>

<style scoped>
.custom-date-picker {
  background-color: rgb(var(--v-theme-background)) !important;
  width: 350px;
  display: grid;
  align-items: center;
}
</style>